import React, { useState, useEffect } from 'react';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import { useCurrency } from '@src/hooks/useCurrency';
import useMediaQuery from '@src/hooks/useMediaQuery';

// Extend window interface for TypeScript
declare global {
  interface Window {
    minicartDiscountThreshold?: number;
  }
}

interface MinicartDiscountsProps {
  cart_subtotal: number;
  block?: any;
  type?: 'minicart' | 'block';
  settings: {
    // Content settings
    show_free_shipping_threshold?: boolean;
    discount_message_alignment?: string;
    discount_message?: string;
    discount_message_style?: string;
    discount_message_style_enable_responsive?: boolean;
    discount_message_style_mobile?: string;
    discount_message_style_tablet?: string;
    discount_message_style_ultrawide?: string;
    discount_message_success?: string;
    discount_message_success_style?: string;
    discount_message_success_style_enable_responsive?: boolean;
    discount_message_success_style_mobile?: string;
    discount_message_success_style_tablet?: string;
    discount_message_success_style_ultrawide?: string;
    discount_threshold?: number;
    bottom_text_alignment?: string;
    bottom_text?: string;
    bottom_text_style?: string;
    bottom_text_style_enable_responsive?: boolean;
    bottom_text_style_mobile?: string;
    bottom_text_style_tablet?: string;
    bottom_text_style_ultrawide?: string;
    bottom_url?: string;

    // Content padding settings
    content_padding_top?: number;
    content_padding_bottom?: number;
    content_padding_left_right?: number;
    free_shipping_padding_top?: number;
    free_shipping_padding_bottom?: number;
    free_shipping_padding_left_right?: number;

    // Appearance settings
    color_schema?: string;
    border_radius?: number;
    progress_bar_background?: string;
    progress_bar_fill?: string;

    // Layout & Spacing settings
    direction?: string;
    padding_top?: number;
    padding_bottom?: number;
    padding_left_right?: number;
    margin_top?: number;
    margin_bottom?: number;
    margin_left_right?: number;

    // Icon settings
    show_icon?: boolean;
    icon?: string;

    // Progress settings
    show_filled_bar?: boolean;

    // Divider settings
    show_divider?: boolean;
    divider_color?: string;
    divider_thickness?: number;

    // Legacy settings (for backward compatibility)
    discount_message_text_style?: string;
    discount_message_success_text_style?: string;
    background_color?: string;
    success_background_color?: string;
    text_color?: string;
    success_text_color?: string;
    amount_bold?: boolean;
  };
}

export const MinicartDiscounts = ({ cart_subtotal, settings, block, type = 'minicart' }: MinicartDiscountsProps) => {
  // Get breakpoints (following TextComponent pattern)
  const breakpoints = {
    mobile_max: 767,
    tablet_min: 768,
    tablet_max: 1023,
    desktop_min: 1024,
    desktop_max: 1439,
    ultrawide_min: 1440
  };

  // Use media queries to determine current breakpoint (following TextComponent pattern)
  const isMobile = useMediaQuery(null, breakpoints.mobile_max);
  const isTablet = useMediaQuery(breakpoints.tablet_min, breakpoints.tablet_max);
  const isDesktop = useMediaQuery(breakpoints.desktop_min, breakpoints.desktop_max);
  const isUltrawide = useMediaQuery(breakpoints.ultrawide_min, null);

  // Function to get responsive text style (following TextComponent pattern)
  const getResponsiveTextStyle = (
    baseStyle: string,
    styleMobile?: string,
    styleTablet?: string,
    styleUltrawide?: string,
    enableResponsiveStyles?: boolean
  ) => {
    if (!enableResponsiveStyles) {
      return baseStyle;
    }

    // Priority order: mobile -> tablet -> desktop -> ultrawide
    // Use inheritance: if a breakpoint style is not set, inherit from the next larger breakpoint
    if (isUltrawide && styleUltrawide) {
      return styleUltrawide;
    }

    if (isDesktop) {
      return baseStyle; // Desktop uses the base style
    }

    if (isTablet && styleTablet) {
      return styleTablet;
    }

    if (isMobile && styleMobile) {
      return styleMobile;
    }

    // Fallback to base style
    return baseStyle;
  };

  if (!settings || !settings.show_free_shipping_threshold) {
    return null;
  }

  // Content settings with responsive support
  const discountMessage =
    settings?.discount_message || 'You need to spend another {difference} to qualify for free shipping';

  const discountMessageTextStyle = getResponsiveTextStyle(
    settings?.discount_message_style || settings?.discount_message_text_style || 'text-base',
    settings?.discount_message_style_mobile,
    settings?.discount_message_style_tablet,
    settings?.discount_message_style_ultrawide,
    settings?.discount_message_style_enable_responsive
  );

  const discountMessageSuccess = settings?.discount_message_success || 'Your order qualifies for free shipping';

  const discountMessageSuccessTextStyle = getResponsiveTextStyle(
    settings?.discount_message_success_style || settings?.discount_message_success_text_style || 'text-base',
    settings?.discount_message_success_style_mobile,
    settings?.discount_message_success_style_tablet,
    settings?.discount_message_success_style_ultrawide,
    settings?.discount_message_success_style_enable_responsive
  );

  const discountThreshold = settings?.discount_threshold || 100;
  const bottomText = settings?.bottom_text;

  // Set the discount threshold globally so other components can access it
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.minicartDiscountThreshold = discountThreshold;
    }
  }, [discountThreshold]);

  const bottomTextStyle = getResponsiveTextStyle(
    settings?.bottom_text_style || 'b9--medium',
    settings?.bottom_text_style_mobile,
    settings?.bottom_text_style_tablet,
    settings?.bottom_text_style_ultrawide,
    settings?.bottom_text_style_enable_responsive
  );

  // Content padding
  const contentPaddingTop = settings?.free_shipping_padding_top ?? settings?.content_padding_top ?? 8;
  const contentPaddingBottom = settings?.free_shipping_padding_bottom ?? settings?.content_padding_bottom ?? 8;
  const contentPaddingLeftRight =
    settings?.free_shipping_padding_left_right ?? settings?.content_padding_left_right ?? 12;

  // Layout & Spacing
  const direction = settings?.direction || 'column';
  const paddingTop = settings?.padding_top ?? 16;
  const paddingBottom = settings?.padding_bottom ?? 16;
  const paddingLeftRight = settings?.padding_left_right ?? 16;
  const marginTop = settings?.margin_top ?? 0;
  const marginBottom = settings?.margin_bottom ?? 20;
  const marginLeftRight = settings?.margin_left_right ?? 0;

  // Appearance
  const borderRadius = settings?.border_radius || 0;
  const progressBarBackground = settings?.progress_bar_background || '#e5e7eb';
  const progressBarFill = settings?.progress_bar_fill || '#10b981';

  // Icon settings
  const showIcon = settings?.show_icon || false;
  const icon = settings?.icon || 'truck';

  // Progress settings
  const showFilledBar = settings?.show_filled_bar !== false;

  // Divider settings
  const showDivider = settings?.show_divider || false;
  const dividerColor = settings?.divider_color || '#e5e7eb';
  const dividerThickness = settings?.divider_thickness || 1;

  // Legacy support
  const backgroundColor = settings?.background_color || '#f9fafb';
  const successBackgroundColor = settings?.success_background_color || '#ecfdf5';
  const textColor = settings?.text_color || '#374151';
  const successTextColor = settings?.success_text_color || '#065f46';
  const amountBold = settings?.amount_bold !== false;

  const thresholdMet = cart_subtotal >= discountThreshold;
  const staticCurrencySymbol = (window as any).store_currency_symbol || '$';
  const { currentCurrencySymbol } = useCurrency();
  const currencySymbolDisplay = staticCurrencySymbol || currentCurrencySymbol;

  // Select the appropriate message and style based on threshold status
  let messageText = '';
  let textStyle = '';

  if (thresholdMet) {
    messageText = discountMessageSuccess;
    textStyle = discountMessageSuccessTextStyle;
  } else {
    const diff = discountThreshold - cart_subtotal;
    const formattedDiff = amountBold
      ? `<strong>${currencySymbolDisplay}${diff.toFixed(2)}</strong>`
      : `${currencySymbolDisplay}${diff.toFixed(2)}`;
    messageText = discountMessage.replace('{difference}', formattedDiff);
    textStyle = discountMessageTextStyle;
  }

  // Calculate progress percentage
  const progressPercentage = Math.min(100, (cart_subtotal / discountThreshold) * 100);

  const containerStyle = {
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeftRight}px`,
    marginRight: `${marginLeftRight}px`,
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingLeftRight}px`,
    paddingRight: `${paddingLeftRight}px`
  } as React.CSSProperties;

  const messageStyle = {
    backgroundColor: thresholdMet ? successBackgroundColor : backgroundColor,
    color: thresholdMet ? successTextColor : textColor,
    display: 'flex',
    flexDirection: direction === 'row' ? 'row' : 'column',
    paddingTop: `${contentPaddingTop}px`,
    paddingBottom: `${contentPaddingBottom}px`,
    paddingLeft: `${contentPaddingLeftRight}px`,
    paddingRight: `${contentPaddingLeftRight}px`,
    borderRadius: `${borderRadius}px`
  };

  const progressBarStyle = {
    backgroundColor: progressBarBackground
  };

  const progressFillStyle = {
    backgroundColor: progressBarFill,
    width: `${progressPercentage}%`
  };

  // Get the correct color schema from block data
  const colorSchema = block?.color_schema || settings?.color_schema;

  // Responsive text styles
  const resolvedDiscountMessageTextStyle = getResponsiveTextStyle(
    discountMessageTextStyle,
    settings?.discount_message_style_mobile,
    settings?.discount_message_style_tablet,
    settings?.discount_message_style_ultrawide,
    settings?.discount_message_style_enable_responsive
  );

  const resolvedDiscountMessageSuccessTextStyle = getResponsiveTextStyle(
    discountMessageSuccessTextStyle,
    settings?.discount_message_success_style_mobile,
    settings?.discount_message_success_style_tablet,
    settings?.discount_message_success_style_ultrawide,
    settings?.discount_message_success_style_enable_responsive
  );

  const resolvedBottomTextStyle = getResponsiveTextStyle(
    bottomTextStyle,
    settings?.bottom_text_style_mobile,
    settings?.bottom_text_style_tablet,
    settings?.bottom_text_style_ultrawide,
    settings?.bottom_text_style_enable_responsive
  );

  // Do not render bottom text if it contains 'Create an Account'
  const hideBottomText = (window as any).customer.isLoggedIn && bottomText && bottomText.includes('Create an Account');

  return (
    <div
      className={`minicart-discounts ${colorSchema ? `color-${colorSchema}` : ''}`}
      style={containerStyle}
    >
      <div
        className='shipping-message block w-full'
        style={messageStyle}
      >
        <div className={`flex items-center ${settings.discount_message_alignment} gap-2`}>
          {showIcon && <i className={`icon icon-${icon}`}></i>}
          <Paragraph
            classes={textStyle}
            text={messageText}
          />
        </div>

        {/* Progress bar */}
        {(!thresholdMet || (thresholdMet && showFilledBar)) && (
          <div className='shipping-progress my-[8px]'>
            <div
              className='shipping-progress-bar h-1 overflow-hidden'
              style={progressBarStyle}
            >
              <div
                className='shipping-progress-fill h-full transition-all duration-300'
                style={progressFillStyle}
              ></div>
            </div>
          </div>
        )}

        {/* Bottom text/link */}
        {!hideBottomText && (
          <div
            className={`${resolvedBottomTextStyle} ${settings.bottom_text_alignment} flex text-[#606060]`}
            style={{ color: thresholdMet ? successTextColor : textColor }}
          >
            <span
              className='[&_a]:underline [&_a]:font-bold'
              dangerouslySetInnerHTML={{ __html: bottomText }}
            />
          </div>
        )}
      </div>

      {showDivider && (
        <div
          style={{
            width: '100%',
            height: dividerThickness,
            backgroundColor: dividerColor
          }}
        />
      )}
    </div>
  );
};
